import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  FormControl,
  FormHelperText,
} from "@mui/material";
import Cropper from "react-easy-crop";
import { useSnackbar } from "../../App";
import { createProduct } from "../../service/productService";
import { getAllBrandLists } from "../../service/brandService";
import { getAllCategoryLists, getAllSubCategory } from "../../service/categoryService";
import Color from "../../components/Color";
import { useMediaQuery, useTheme } from "@mui/material";

// Helper to crop image to fixed size 185x140
const getCroppedImg = (imageSrc, croppedAreaPixels) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = 185;
      canvas.height = 140;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        185,
        140
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas is empty"));
          const file = new File([blob], "cropped.png", { type: "image/png" });
          const url = URL.createObjectURL(blob);
          resolve({ file, url });
        },
        "image/png",
        1.0
      );
    };

    image.onerror = (err) => reject(err);
  });
};

const AddProduct = ({ open, onClose, onSuccess, defaultCategory }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const productCodeInputRef = useRef(null);

  const initialFormState = {
    name: "",
    product_code: "",
    base_price: "",
    tax_rate: "",
    brand_id: "",
    category_id: defaultCategory ? defaultCategory.id : "",
    description: "",
    is_active: true,
    sub_category_id: "",
  };

  const [form, setForm] = useState(initialFormState);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [rawImageUrl, setRawImageUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    const fetchDropdowns = async () => {
      setCategoriesLoading(true);
      try {
        const brandRes = await getAllBrandLists();
        const categoryRes = await getAllCategoryLists();
        const fetchedCategories = Array.isArray(categoryRes?.data?.data) ? categoryRes.data.data : [];
        setBrands(Array.isArray(brandRes?.data?.data) ? brandRes.data.data : []);
        setCategories(fetchedCategories);

        if (defaultCategory && !fetchedCategories.find((cat) => cat.id === defaultCategory.id)) {
          showSnackbar(`Category ID ${defaultCategory.id} is invalid. Please select a valid category.`, "error");
          setForm((prev) => ({ ...prev, category_id: "" }));
        }

        if (form.category_id) {
          setSubCategoriesLoading(true);
          const subCategoryRes = await getAllSubCategory(form.category_id);
          const fetchedSubCategories = Array.isArray(subCategoryRes?.data?.data) ? subCategoryRes.data.data : [];
          setSubCategories(fetchedSubCategories);

          // If category is "Cake" (case-insensitive) and subcategories are available, set the first one as default
          const selectedCategory = fetchedCategories.find((cat) => cat.id === form.category_id);
          if (
            selectedCategory?.name?.toLowerCase() === "cake" &&
            fetchedSubCategories.length > 0 &&
            !form.sub_category_id
          ) {
            setForm((prev) => ({ ...prev, sub_category_id: fetchedSubCategories[0].id }));
          }
        }
      } catch {
        showSnackbar("Failed to load dropdown data", "error");
      } finally {
        setCategoriesLoading(false);
        setSubCategoriesLoading(false);
      }
    };
    if (open) {
      fetchDropdowns();
      resetForm();
    }
  }, [open, showSnackbar, defaultCategory]);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (form.category_id) {
        setSubCategoriesLoading(true);
        try {
          const subCategoryRes = await getAllSubCategory(form.category_id);
          const fetchedSubCategories = Array.isArray(subCategoryRes?.data?.data) ? subCategoryRes.data.data : [];
          setSubCategories(fetchedSubCategories);

          // If category is "Cake" (case-insensitive) and subcategories are available, set the first one as default
          const selectedCategory = categories.find((cat) => cat.id === form.category_id);
          if (
            selectedCategory?.name?.toLowerCase() === "cake" &&
            fetchedSubCategories.length > 0 &&
            !form.sub_category_id
          ) {
            setForm((prev) => ({ ...prev, sub_category_id: fetchedSubCategories[0].id }));
          }
        } catch {
          showSnackbar("Failed to load subcategories", "error");
        } finally {
          setSubCategoriesLoading(false);
        }
      } else {
        setSubCategories([]);
      }
    };
    fetchSubCategories();
  }, [form.category_id, showSnackbar, categories]);

  const resetForm = () => {
    setForm({
      ...initialFormState,
      category_id: defaultCategory ? defaultCategory.id : "",
    });
    setErrors({});
    setImageFile(null);
    setUploadedImageUrl(null);
    setRawImageUrl(null);
    setCropDialogOpen(false);
    setCroppedAreaPixels(null);
    setSubCategories([]);
  };

  const handleChange = (field, value) => {
    if (field === "name") {
      const filteredValue = value.replace(/[^a-zA-Z ]/g, "");
      setForm((prev) => ({ ...prev, [field]: filteredValue }));
      if (/[^a-zA-Z ]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          name: ["Only letters are allowed"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (field === "base_price" || field === "tax_rate") {
      const filteredValue = value.replace(/[^0-9.]/g, "");
      setForm((prev) => ({ ...prev, [field]: filteredValue }));
      if (/[^0-9.]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          [field]: ["Only numbers are allowed"],
        }));
      } else if (field === "base_price" && (filteredValue === "" || parseFloat(filteredValue) <= 0)) {
        setErrors((prev) => ({
          ...prev,
          base_price: ["Price must be greater than 0"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      if (field === "category_id") {
        setForm((prev) => ({ ...prev, sub_category_id: "" }));
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showSnackbar("Please upload a valid image file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar("Image size must be less than 5MB", "error");
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    img.onload = () => {
      const width = img.width;
      const height = img.height;

      if (width === 185 && height === 140) {
        setImageFile(file);
        setUploadedImageUrl(objectUrl);
        setCropDialogOpen(false);
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          setRawImageUrl(reader.result);
          setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
      }
    };

    img.onerror = () => {
      showSnackbar("Failed to read image dimensions", "error");
      URL.revokeObjectURL(objectUrl);
    };
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropConfirm = async () => {
    try {
      const { file, url } = await getCroppedImg(rawImageUrl, croppedAreaPixels);
      setImageFile(file);
      setUploadedImageUrl(url);
      setCropDialogOpen(false);
    } catch {
      showSnackbar("Failed to crop image", "error");
    }
  };

  const validate = () => {
    const newErrors = {};
    const selectedCategory = categories.find((cat) => cat.id === form.category_id);

    if (!form.name.trim()) newErrors.name = ["Product name is required"];
    else if (!/^[a-zA-Z ]+$/.test(form.name)) newErrors.name = ["Only letters are allowed"];

    if (!form.product_code.trim()) newErrors.product_code = ["Product code is required"];

    if (!form.base_price) newErrors.base_price = ["Price is required"];
    else if (parseFloat(form.base_price) <= 0) newErrors.base_price = ["Price must be greater than 0"];

    if (form.tax_rate && isNaN(parseFloat(form.tax_rate))) {
      newErrors.tax_rate = ["Tax rate must be a valid number"];
    } else if (form.tax_rate && parseFloat(form.tax_rate) < 0) {
      newErrors.tax_rate = ["Tax rate cannot be negative"];
    }

    if (!form.brand_id) newErrors.brand_id = ["Brand is required"];
    if (!form.category_id) newErrors.category_id = ["Category is required"];
    else if (!categories.find((cat) => cat.id === form.category_id)) {
      newErrors.category_id = ["The selected category ID is invalid"];
    }

    if (!form.description.trim()) newErrors.description = ["Description is required"];

    if (selectedCategory?.name?.toLowerCase() === "cake" || defaultCategory?.name?.toLowerCase() === "cake") {
      if (!form.sub_category_id) newErrors.sub_category_id = ["Subcategory is required for cake"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    const formData = new FormData();
    for (let key in form) {
      if (key === "category_id" || key === "brand_id" || key === "sub_category_id") {
        formData.append(key, parseInt(form[key], 10) || "");
      } else {
        formData.append(key, form[key]);
      }
    }
    if (imageFile) formData.append("image", imageFile);

    try {
      const res = await createProduct(formData);
      const createdProduct = res.data.data;
      showSnackbar("Product added successfully!", "success");
      setUploadedImageUrl(createdProduct.image_url);
      resetForm();
      if (onSuccess) onSuccess(createdProduct);
      setTimeout(() => onClose(), 800);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
        if (err.response.data.errors.product_code?.includes("The product code has already been taken.")) {
          productCodeInputRef.current?.focus();
          showSnackbar("The product code is already taken. Please use a different product code.", "error");
        } else {
          showSnackbar("Validation failed. Please check the form.", "error");
        }
      } else {
        showSnackbar(err.response?.data?.message || "Something went wrong", "error");
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (Object.keys(errors).length > 0 && (reason === "backdropClick" || reason === "escapeKeyDown")) {
            showSnackbar("Please fix validation errors before closing.", "error");
            return;
          }
          handleCancel();
        }}
        disableEscapeKeyDown={Object.keys(errors).length > 0}
        PaperProps={{
          sx: { width: isMobile ? "90vw" : "1100px", maxWidth: "95vw", borderRadius: "8px" },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Create Product
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: isMobile ? 2 : 3,
            py: isMobile ? 1 : 2,
            maxHeight: isMobile ? "70vh" : "auto",
            overflowY: "auto",
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {/* Row 1: Product Name and Product Code */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Product Name<span style={{color:'red', fontSize:'14px'}}>*</span>
                </Typography>
                <TextField
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Enter product name"
                  error={!!errors.name}
                  helperText={errors.name?.[0]}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ "aria-describedby": errors.name ? "name-error" : undefined }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    mb: 0.8,
                    "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "error.main",
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Product Code<span style={{color:'red', fontSize:'14px'}}>*</span>
                </Typography>
                <TextField
                  value={form.product_code}
                  onChange={(e) => handleChange("product_code", e.target.value)}
                  placeholder="Enter product code"
                  error={!!errors.product_code}
                  helperText={errors.product_code?.[0]}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputRef={productCodeInputRef}
                  inputProps={{ "aria-describedby": errors.product_code ? "product-code-error" : undefined }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    mb: 0.8,
                    "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "error.main",
                    },
                  }}
                />
              </Box>

              {/* Row 2: Price and Tax Rate */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Price<span style={{color:'red', fontSize:'14px'}}>*</span>
                </Typography>
                <TextField
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.base_price}
                  onChange={(e) => handleChange("base_price", e.target.value)}
                  placeholder="Enter price"
                  error={!!errors.base_price}
                  helperText={errors.base_price?.[0]}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ "aria-describedby": errors.base_price ? "base-price-error" : undefined }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    mb: 0.8,
                    "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "error.main",
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Tax Rate (%)
                </Typography>
                <TextField
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.tax_rate}
                  onChange={(e) => handleChange("tax_rate", e.target.value)}
                  placeholder="Enter tax rate"
                  error={!!errors.tax_rate}
                  helperText={errors.tax_rate?.[0]}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ "aria-describedby": errors.tax_rate ? "tax-rate-error" : undefined }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    mb: 0.8,
                    "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "error.main",
                    },
                  }}
                />
              </Box>

              {/* Row 3: Brand and Category */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Brand<span style={{color:'red', fontSize:'14px'}}>*</span>
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.brand_id} sx={{ mb: 0.8 }}>
                  <Select
                    value={form.brand_id ?? ""}
                    onChange={(e) => handleChange("brand_id", e.target.value)}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return <Box sx={{ color: "text.secondary" }}>Select brand</Box>;
                      }
                      return brands.find((brand) => brand.id === value)?.name || "";
                    }}
                  >
                    <MenuItem value="" disabled>
                      {categoriesLoading ? "Loading brands..." : "Select brand"}
                    </MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                    ))}
                  </Select>
                  {errors.brand_id && <FormHelperText>{errors.brand_id[0]}</FormHelperText>}
                </FormControl>
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Category<span style={{color:'red', fontSize:'14px'}}>*</span>
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.category_id} sx={{ mb: 0.8 }}>
                  <Select
                    value={form.category_id ?? ""}
                    onChange={(e) => {
                      handleChange("category_id", e.target.value);
                      handleChange("sub_category_id", "");
                    }}
                    displayEmpty
                    disabled={!!defaultCategory || categoriesLoading}
                    renderValue={(value) => {
                      if (!value) {
                        return <Box sx={{ color: "text.secondary" }}>Select category</Box>;
                      }
                      return (
                        categories.find((cat) => cat.id === value)?.name ||
                        (defaultCategory && value === defaultCategory.id ? defaultCategory.name : "Loading...")
                      );
                    }}
                  >
                    <MenuItem value="" disabled>
                      {categoriesLoading ? "Loading categories..." : "Select category"}
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                    ))}
                  </Select>
                  {errors.category_id && <FormHelperText>{errors.category_id[0]}</FormHelperText>}
                </FormControl>
              </Box>

              {/* Subcategory and Description */}
              {(() => {
                const selectedCategory = categories.find((cat) => cat.id === form.category_id);
                if (
                  selectedCategory?.name?.toLowerCase() === "cake" ||
                  defaultCategory?.name?.toLowerCase() === "cake"
                ) {
                  return (
                    <>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 1,
                            fontWeight: "bold",
                            color: Color.label.main,
                            fontSize: isMobile ? "12px" : "13px",
                          }}
                        >
                          Subcategory
                        </Typography>
                        <FormControl fullWidth size="small" error={!!errors.sub_category_id} sx={{ mb: 0.8 }}>
                          <Select
                            value={form.sub_category_id ?? ""}
                            onChange={(e) => handleChange("sub_category_id", e.target.value)}
                            displayEmpty
                            disabled={subCategoriesLoading}
                            renderValue={(value) => {
                              if (!value) {
                                return (
                                  <Box sx={{ color: "text.secondary" }}>
                                    {subCategoriesLoading ? "Loading subcategories..." : "Select subcategory"}
                                  </Box>
                                );
                              }
                              return subCategories.find((subcat) => subcat.id === value)?.name || "";
                            }}
                          >
                            <MenuItem value="" disabled>
                              {subCategoriesLoading ? "Loading subcategories..." : "Select subcategory"}
                            </MenuItem>
                            {subCategories.length === 0 && !subCategoriesLoading && (
                              <MenuItem value="" disabled>
                                No subcategories available
                              </MenuItem>
                            )}
                            {subCategories.map((subcat) => (
                              <MenuItem key={subcat.id} value={subcat.id}>{subcat.name}</MenuItem>
                            ))}
                          </Select>
                          {errors.sub_category_id && <FormHelperText>{errors.sub_category_id[0]}</FormHelperText>}
                        </FormControl>
                      </Box>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 1,
                            fontWeight: "bold",
                            color: Color.label.main,
                            fontSize: isMobile ? "12px" : "13px",
                          }}
                        >
                          Description<span style={{color:'red', fontSize:'14px'}}>*</span>
                        </Typography>
                        <TextField
                          multiline
                          rows={3}
                          value={form.description}
                          onChange={(e) => handleChange("description", e.target.value)}
                          placeholder="Enter description"
                          error={!!errors.description}
                          helperText={errors.description?.[0]}
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{ shrink: false }}
                          inputProps={{ "aria-describedby": errors.description ? "description-error" : undefined }}
                          sx={{
                            "& .MuiInputLabel-root": { display: "none" },
                            mb: 0.8,
                            "& .MuiInputBase-inputMultiline": {
                              paddingTop: "8px",
                              paddingBottom: "8px",
                              lineHeight: 1.5,
                              fontSize: isMobile ? "0.85rem" : "1rem",
                            },
                            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                              borderColor: "error.main",
                            },
                          }}
                        />
                      </Box>
                    </>
                  );
                }
                return (
                  <Box sx={{ gridColumn: { sm: "span 2" } }}>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: Color.label.main,
                        fontSize: isMobile ? "12px" : "13px",
                      }}
                    >
                      Description <span style={{color:'red', fontSize:'14px'}}>*</span>
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      value={form.description}
                      onChange={(e) => handleChange("description", e.target.value)}
                      placeholder="Enter description"
                      error={!!errors.description}
                      helperText={errors.description?.[0]}
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ "aria-describedby": errors.description ? "description-error" : undefined }}
                      sx={{
                        "& .MuiInputLabel-root": { display: "none" },
                        mb: 0.8,
                        "& .MuiInputBase-inputMultiline": {
                          paddingTop: "8px",
                          paddingBottom: "8px",
                          lineHeight: 1.5,
                          fontSize: isMobile ? "0.85rem" : "1rem",
                        },
                        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                          borderColor: "error.main",
                        },
                      }}
                    />
                  </Box>
                );
              })()}
            </Box>

            {/* Active/Inactive Switch */}
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Status
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => handleChange("is_active", e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: Color.primary.main,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: Color.primary.main,
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: "#ccc",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: form.is_active ? Color.primary.main : "text.secondary",
                      fontWeight: form.is_active ? "bold" : "normal",
                      fontSize: isMobile ? "0.85rem" : "1rem",
                    }}
                  >
                    {form.is_active ? "Active" : "Inactive"}
                  </Typography>
                }
              />
            </Box>

            {/* Image Upload */}
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Product Image (optional)
              </Typography>
              <Button
                variant="contained"
                component="label"
                sx={{
                  mb: 0.8,
                  background: Color.primary.main,
                  color: "white",
                  "&:hover": { background: Color.primary.dark },
                }}
              >
                Choose Image
                <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
              </Button>
              {errors.image &&
                errors.image.map((msg, i) => (
                  <Typography
                    key={i}
                    variant="caption"
                    color="error"
                    sx={{ display: "block" }}
                  >
                    {msg}
                  </Typography>
                ))}
              <Typography sx={{ fontSize: isMobile ? 8 : 10, mt: 0 }}>
                Upload image with dimensions 185x140 pixels
              </Typography>
            </Box>
            {uploadedImageUrl && (
              <Box mt={1}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Current Product Image
                </Typography>
                <img
                  src={uploadedImageUrl}
                  alt="Uploaded Preview"
                  style={{
                    width: isMobile ? "50%" : "30%",
                    maxHeight: 230,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
              </Box>
            )}

            {/* Submit and Cancel Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={1} mb={1}>
              <Button
                onClick={handleCancel}
                sx={{
                  background: "none",
                  color: Color.secondary.main,
                  border: "1px solid",
                  borderColor: Color.secondary.main,
                  width: isMobile ? "100%" : "auto",
                  fontSize: isMobile ? "0.85rem" : "1rem",
                  padding: isMobile ? "8px" : "6px 16px",
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                sx={{
                  background: Color.primary.main,
                  color: "white",
                  width: isMobile ? "100%" : "auto",
                  fontSize: isMobile ? "0.85rem" : "1rem",
                  padding: isMobile ? "8px" : "6px 16px",
                  "&:hover": { background: Color.primary.dark },
                }}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Crop Dialog */}
      <Dialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { width: isMobile ? "90vw" : "300", borderRadius: "8px" },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Crop Image
        </DialogTitle>
        <DialogContent>
          <div style={{ position: "relative", width: "100%", height: 300 }}>
            <Cropper
              image={rawImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={185 / 140}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        </DialogContent>
        <DialogActions
          sx={{
            mb: 2,
            mr: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.secondary.main,
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
            onClick={() => setCropDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": { background: Color.primary.dark },
            }}
            onClick={handleCropConfirm}
          >
            Crop
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddProduct;